import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getUserRoles } from '../../../utils/userRoles';
import {
  fetchScopedPlayersByStatus,
  updateScopedPlayerApproval,
} from '../../../services/playerApprovalsService';

const statusClassMap = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const scopeLabelMap = {
  community: 'Community',
  organization: 'Organization',
  club: 'Club',
};

const getScopeIdFromParams = (scopeType, params) => {
  if (scopeType === 'organization') return params.organizationId;
  if (scopeType === 'community') return params.communityId;
  if (scopeType === 'club') return params.clubId;
  return null;
};

const PlayerApprovalsPage = ({ scopeType }) => {
  const params = useParams();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState('');
  const [loadError, setLoadError] = useState('');
  const [status, setStatus] = useState({ message: '', tone: 'info' });

  const scopeId = getScopeIdFromParams(scopeType, params);
  const userRoles = getUserRoles(user);
  const isDepartmentAdmin = userRoles.has('department_admin');
  const isScopedAdmin = userRoles.has(`${scopeType}_admin`);
  const isSuperAdmin = userRoles.has('super_admin');
  const canManageApprovals = isDepartmentAdmin || isScopedAdmin || isSuperAdmin;
  const contextType = isDepartmentAdmin ? 'department_admin' : `${scopeType}_admin`;

  useEffect(() => {
    const loadPlayers = async () => {
      if (!token || !scopeId || !canManageApprovals) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchScopedPlayersByStatus({
          scopeType,
          scopeId,
          contextType,
          entityType: scopeType,
          entityId: scopeId,
          status: activeTab,
          token,
        });

        setPlayers(response.players || []);
      } catch (error) {
        setLoadError(error.message || 'Unable to load players for approval.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlayers();
  }, [activeTab, canManageApprovals, contextType, scopeId, scopeType, token]);

  const tabCounts = useMemo(
    () => ({
      pending: players.filter((player) => player.approvalStatus === 'pending').length,
      approved: players.filter((player) => player.approvalStatus === 'approved').length,
    }),
    [players]
  );

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canManageApprovals) {
    return <Navigate to="/" replace />;
  }

  const scopeLabel = scopeLabelMap[scopeType] || 'Department';

  const handleUpdateStatus = async (playerId, action) => {
    setIsUpdating(`${playerId}:${action}`);
    setStatus({ message: '', tone: 'info' });

    try {
      const response = await updateScopedPlayerApproval({
        scopeType,
        scopeId,
        contextType,
        entityType: scopeType,
        entityId: scopeId,
        playerId,
        action,
        token,
      });

      const updatedPlayer = response.player;
      setPlayers((prev) => {
        if (activeTab === 'pending') {
          return prev.filter((player) => player.id !== updatedPlayer.id);
        }

        return prev.map((player) => (player.id === updatedPlayer.id ? updatedPlayer : player));
      });

      setStatus({
        message: action === 'approve' ? 'Player approved successfully.' : 'Player rejected successfully.',
        tone: 'success',
      });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to update player approval.', tone: 'danger' });
    } finally {
      setIsUpdating('');
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body d-grid gap-4">
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2">
          <div>
            <p className="eyebrow text-primary mb-1">Department admin</p>
            <h1 className="h4 mb-2">{scopeLabel} player approvals</h1>
            <p className="text-muted mb-0">Review and manage pending player approvals for this {scopeLabel.toLowerCase()}.</p>
          </div>
          <span className="badge bg-primary-subtle text-primary-emphasis">Approval queue</span>
        </div>

        <div className="d-flex flex-wrap gap-2">
          {[
            { key: 'pending', label: 'Pending' },
            { key: 'approved', label: 'Approved' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`btn ${activeTab === tab.key ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {activeTab === tab.key ? '' : ` (${tabCounts[tab.key] || 0})`}
            </button>
          ))}
        </div>

        {status.message && (
          <div className={`alert alert-${status.tone === 'danger' ? 'danger' : 'success'} py-2 mb-0`}>
            {status.message}
          </div>
        )}

        {loadError && <div className="alert alert-danger py-2 mb-0">{loadError}</div>}

        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col">Email</th>
                <th scope="col">Primary role</th>
                <th scope="col">Status</th>
                <th scope="col" className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="5" className="text-center text-muted">Loading players...</td>
                </tr>
              )}
              {!isLoading && players.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    {activeTab === 'pending' ? 'No pending approvals in this scope.' : 'No approved players found in this scope.'}
                  </td>
                </tr>
              )}
              {players.map((player) => {
                const displayName = player.playerProfile?.fullName || player.name || 'Unnamed player';
                const primaryRole = player.playerProfile?.primaryRole || 'Not provided';
                const badgeClass = statusClassMap[player.approvalStatus] || 'secondary';
                const isApproveLoading = isUpdating === `${player.id}:approve`;
                const isRejectLoading = isUpdating === `${player.id}:reject`;

                return (
                  <tr key={player.id}>
                    <td>
                      <div className="fw-semibold">{displayName}</div>
                      <div className="text-muted small">Joined {new Date(player.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td>{player.email}</td>
                    <td>{primaryRole}</td>
                    <td>
                      <span className={`badge text-bg-${badgeClass} text-capitalize`}>{player.approvalStatus}</span>
                    </td>
                    <td>
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          disabled={player.approvalStatus === 'approved' || isApproveLoading || isRejectLoading}
                          onClick={() => handleUpdateStatus(player.id, 'approve')}
                        >
                          {isApproveLoading ? 'Approving...' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={player.approvalStatus === 'rejected' || isApproveLoading || isRejectLoading}
                          onClick={() => handleUpdateStatus(player.id, 'reject')}
                        >
                          {isRejectLoading ? 'Rejecting...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerApprovalsPage;
